/**
 * Модальное окно создания нового отдела.
 * Позволяет ввести название отдела и выбрать, к какому КБ он относится.
 * Если КБ не выбрано, отдел создается "вне КБ" (kbId = null).
 */
import { useCallback } from "react";
import { ModalShell } from "../ModalShell";
import { FormField } from "../FormField";
import { useModalForm } from "../../hooks/useModalForm";
import { useUpsertDepartmentMutation } from "../../api/queries/departments";
import { useKbsQuery } from "../../api/queries/kbs";
import { uid } from "../../utils/date";
import { TOASTS } from "../../utils/constants";

const FIELDS = Object.freeze(["name", "kbId"]);

export const CreateDeptModal = ({ onClose, toast }) => {
  const upsertMutation = useUpsertDepartmentMutation();
  const kbsQ = useKbsQuery();
  const kbs = kbsQ.data ?? [];

  const initialValues = {
    name: "",
    kbId: "none",
  };

  const validate = useCallback((values) => {
    const errors = {};
    if (!values.name.trim()) {
      errors.name = "Название отдела обязательно";
    }
    return errors;
  }, []);

  const saveAsync = useCallback(
    async (vals) => {
      await upsertMutation.mutateAsync({
        id: "d_" + uid(),
        body: {
          name: vals.name.trim(),
          kbId: vals.kbId === "none" ? null : vals.kbId,
        },
      });
      toast(TOASTS.deptCreated(vals.name.trim()), "success");
      onClose();
    },
    [upsertMutation, onClose, toast]
  );

  const { values, handleChange, errors, touched, handleSubmit, isSubmitting } =
    useModalForm(
      initialValues,
      validate,
      saveAsync,
      (error) => toast(error.message || "Ошибка создания отдела", "error"),
      { fields: FIELDS }
    );

  const kbOptions = [
    { value: "none", label: "Без КБ (вне КБ)" },
    ...kbs.map((kb) => ({ value: kb.id, label: kb.name })),
  ];

  return (
    <ModalShell
      title="Новый отдел"
      onClose={onClose}
      onSave={handleSubmit()}
      saveLabel="Создать"
      width={460}
      className="modal-dept"
      saveDisabled={isSubmitting}
    >
      <div className="project-info-fields">
        <FormField
          label="Название отдела"
          required
          value={values.name}
          onChange={(v) => handleChange("name", v)}
          error={touched.name && errors.name}
          placeholder="Например: Отдел аэродинамики"
          inline
        />
        <FormField
          label="Конструкторское бюро"
          type="select"
          value={values.kbId}
          onChange={(v) => handleChange("kbId", v)}
          options={kbOptions}
          inline
        />
      </div>
    </ModalShell>
  );
};
